import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type { MCPServerListItem } from '@kirin/api-client/contracts/mcp';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useReleasedPlatforms } from '@/components/shared/platform/useReleasedPlatforms';
import { createPersonalMCPColumns } from './columns';
import { fetchPersonalMCPServers } from './api';
import { MCP_TABLE_CONFIG } from './constants';

function getQueryKeysFromParams(params: DataFetchParams) {
  return queryKeys.mcp.personalDataTable({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
}

/**
 * DataTable-based MCP servers tab for the user dashboard.
 * Shows personal MCP servers with filtering, sorting, and search.
 */
export function DataTablePersonalMCPContent(): ReactElement {
  const releasedPlatforms = useReleasedPlatforms();
  const columns = useMemo(
    () => createPersonalMCPColumns(releasedPlatforms),
    [releasedPlatforms]
  );

  return (
    <DataTable<MCPServerListItem>
      id={MCP_TABLE_CONFIG.tableId}
      mode="server"
      className="flex flex-col flex-1 min-h-0"
      containerClassName="flex-1"
      columns={columns}
      errorBoundaryLabel="Something went wrong in the MCP servers table"
      search={{ placeholder: 'Search MCP servers...', className: 'w-[260px]' }}
      features={{ enableSorting: true }}
      queryFn={(params, signal) => fetchPersonalMCPServers(params, signal)}
      getQueryKeysFromParams={getQueryKeysFromParams}
      noDataLabel="No MCP servers configured. Configure MCP servers in your IDE to see them here."
      queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
    />
  );
}

DataTablePersonalMCPContent.displayName = 'DataTablePersonalMCPContent';

export default function DataTableMCPTab(): ReactElement {
  return <DataTablePersonalMCPContent />;
}
